import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function SharedDepsPage(): Node {
  return html`
    <div>
      <h1>Shared Dependencies</h1>
      <p>When multiple remotes use onefold (or other shared libraries), Import Maps prevent duplicate downloads and ensure a single instance.</p>

      <h2>The Problem</h2>
      <p>Without sharing, each remote bundles its own copy of onefold. This means:</p>
      <ul>
        <li>3kb × N remotes downloaded redundantly.</li>
        <li>Multiple signal systems — signals don't cross remote boundaries.</li>
        <li>Larger memory footprint.</li>
      </ul>

      <h2>Import Maps Solution</h2>
      ${CodeBlock(`<!-- index.html (host) -->
<script type="importmap">
{
  "imports": {
    "onefold": "https://cdn.example.com/onefold@1.2.0/index.js"
  }
}
</script>`)}

      <p>Remotes use bare imports that resolve via the map:</p>
      ${CodeBlock(`// Remote widget — bare import (resolved by import map)
import { html, createSignal } from 'onefold';

export default function Widget(): Node {
  const count = createSignal(0);
  return html\`<button onclick=\${() => count.set(n => n + 1)}>\${() => count()}</button>\`;
}`)}

      <h2>Comparison</h2>
      <table>
        <tr><th>Approach</th><th>Bundle Size</th><th>Shared State</th><th>Complexity</th></tr>
        <tr><td>Each remote bundles onefold</td><td>Large (duplicated)</td><td>No</td><td>Low</td></tr>
        <tr><td>Import Map (shared)</td><td>Minimal (single copy)</td><td>Yes</td><td>Medium</td></tr>
        <tr><td>External (CDN + global)</td><td>Minimal</td><td>Yes</td><td>Medium</td></tr>
      </table>

      <h2>Versioning Strategy</h2>
      <p>Pin the shared dependency to a specific version in the import map:</p>
      ${CodeBlock(`{
  "imports": {
    "onefold": "https://cdn.example.com/onefold@1.2.0/index.js"
  }
}`)}
      <ul>
        <li><strong>Patch updates</strong> — safe to update the import map URL. Remotes don't need redeployment.</li>
        <li><strong>Minor updates</strong> — generally safe. Test remotes against the new version first.</li>
        <li><strong>Major updates</strong> — coordinate with all teams. Update remotes before changing the map.</li>
      </ul>

      ${Callout('Import Maps are supported in all modern browsers. For older browsers, use the es-module-shims polyfill.')}

      <h2>Build Configuration</h2>
      <p>Mark <code>onefold</code> as external in your remote's build config so it's not bundled:</p>
      ${CodeBlock(`// esbuild config for remote
import esbuild from 'esbuild';

esbuild.build({
  entryPoints: ['src/index.ts'],
  bundle: true,
  format: 'esm',
  external: ['onefold'],  // Don't bundle — resolved via import map
  outfile: 'dist/widget.js',
});`)}
    </div>
  `;
}
